/**
 * AdminAISearchCard — admin controls for the AI vendor search (kill-switch +
 * per-identity daily quota). Also surfaces last-24h usage stats.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkle, FloppyDisk, Spinner } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAISearchCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [limit, setLimit] = useState(10);
  const [usage, setUsage] = useState({ total_searches: 0, distinct_logged_in_users: 0, distinct_anonymous_ips: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/ai-search`, { withCredentials: true });
      setEnabled(data.settings.enabled !== false);
      setLimit(Number(data.settings.daily_limit_per_user) || 0);
      setUsage(data.usage_24h || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load AI search settings');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/ai-search`,
        { enabled, daily_limit_per_user: Math.max(0, Number(limit) || 0) },
        { withCredentials: true });
      toast.success('AI search settings saved');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-ai-search-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <Sparkle size={20} className="text-brand-primary" weight="fill" /> AI Vendor Search
        </CardTitle>
        <CardDescription>
          Gemini-powered grounded search on the Instant Pricing page. Toggle it off
          to hide the search box entirely, or cap how many queries each user
          (or anonymous IP) can run per 24 hours.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-text-secondary py-4 text-center">Loading…</p>
        ) : (
          <div className="space-y-5">
            {/* Kill-switch */}
            <div className="flex items-center justify-between p-3 border border-[#E8E1D5]">
              <div>
                <div className="text-sm font-medium">AI search enabled</div>
                <div className="text-[11px] text-text-secondary">
                  When off, the search box is hidden everywhere and API calls return 503.
                </div>
              </div>
              <Switch checked={enabled} onCheckedChange={setEnabled}
                data-testid="admin-ai-search-enabled-switch" />
            </div>

            {/* Daily limit */}
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-text-secondary">
                Daily searches per user (0 = unlimited)
              </label>
              <Input
                type="number" min="0" max="10000"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                className="h-10 rounded-none border-[#E8E1D5] mt-1 w-32"
                data-testid="admin-ai-search-limit-input"
                disabled={!enabled}
              />
              <p className="text-[10px] text-text-secondary mt-1">
                Counted per logged-in user, or per IP for anonymous visitors. Resets on a rolling 24h window.
              </p>
            </div>

            {/* Usage */}
            <div className="grid grid-cols-3 gap-3 pt-3 border-t border-[#E8E1D5]">
              <div className="text-center">
                <div className="text-2xl font-heading text-brand-primary" data-testid="admin-ai-search-usage-total">
                  {usage.total_searches}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-text-secondary">
                  Total searches (24h)
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-heading text-text-primary">
                  {usage.distinct_logged_in_users}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-text-secondary">
                  Unique users
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-heading text-text-primary">
                  {usage.distinct_anonymous_ips}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-text-secondary">
                  Anonymous IPs
                </div>
              </div>
            </div>

            <Button onClick={save} disabled={saving}
              className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 disabled:opacity-60"
              data-testid="admin-ai-search-save-btn">
              {saving ? <Spinner size={14} className="mr-2 animate-spin" /> : <FloppyDisk size={14} className="mr-2" weight="fill" />}
              Save settings
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminAISearchCard;
